import React, { useState, useEffect } from 'react';
import { eventsApi } from '../services/api';

const DEFAULT_HERO = 'https://firebasestorage.googleapis.com/v0/b/daycare-39d32.firebasestorage.app/o/images%2Fdaycare1.png?alt=media';

const FACILITY_COLORS = [
  '#e879a0','#f97316','#f59e0b','#10b981',
  '#14b8a6','#6366f1','#8b5cf6','#0ea5e9','#ef4444','#84cc16',
];

function waLink(phone, name) {
  const digits = (phone || '').replace(/\D/g, '');
  const num = digits.length === 10 ? `91${digits}` : digits;
  const msg = encodeURIComponent(`Hello! I'm interested in enrolling my child at ${name}. Could you please share more details?`);
  return `https://wa.me/${num}?text=${msg}`;
}

// Convert a Google Maps share URL to an embeddable src
function toMapEmbedUrl(url) {
  if (!url) return null;
  try {
    // Already an embed URL
    if (url.includes('/maps/embed') || url.includes('output=embed')) return url;
    // Extract @lat,lng from standard share URLs
    const coord = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (coord) {
      return `https://maps.google.com/maps?q=${coord[1]},${coord[2]}&output=embed&z=16`;
    }
    // Extract ?q= parameter
    const u = new URL(url);
    const q = u.searchParams.get('q');
    if (q) return `https://maps.google.com/maps?q=${encodeURIComponent(q)}&output=embed&z=15`;
    // Extract /place/NAME/ segment
    const placeM = url.match(/\/place\/([^/@?]+)/);
    if (placeM) {
      const placeName = decodeURIComponent(placeM[1].replace(/\+/g, ' '));
      return `https://maps.google.com/maps?q=${encodeURIComponent(placeName)}&output=embed&z=15`;
    }
  } catch (_) {}
  return null;
}

export default function SchoolDetail({ school, onBack, onBook, canBook, activeUser, myBookings = [] }) {
  const slides = school.gallery && school.gallery.length > 0
    ? school.gallery
    : null;

  const [slideIdx,      setSlideIdx]      = useState(0);
  const [visible,       setVisible]       = useState(false);
  const [lightboxIdx,   setLightboxIdx]   = useState(null); // null = closed
  const [publicEvents,  setPublicEvents]  = useState([]);
  const [openEvtId,     setOpenEvtId]     = useState(null); // photo collection open
  const [evtLbIdx,      setEvtLbIdx]      = useState(null); // lightbox photo index
  // schoolId must be declared before useEffect that depends on it
  const today     = new Date().toISOString().split('T')[0];
  const schoolId  = school.id || school.schoolId;
  const hasBookingToday = activeUser?.userType === 'parent' && myBookings.some(
    b => (b.schoolId === schoolId) && b.date === today && b.status !== 'cancelled'
  );

  // Close collection on Escape
  useEffect(() => {
    if (!openEvtId) return;
    const onKey = (e) => { if (e.key === 'Escape') { setEvtLbIdx(null); setOpenEvtId(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openEvtId]);

  // Keyboard nav for event lightbox
  useEffect(() => {
    if (evtLbIdx === null) return;
    const ev = publicEvents.find(e => e.id === openEvtId);
    const imgs = (ev?.images || []);
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setEvtLbIdx(i => (i + 1) % imgs.length);
      if (e.key === 'ArrowLeft')  setEvtLbIdx(i => (i - 1 + imgs.length) % imgs.length);
      if (e.key === 'Escape')     setEvtLbIdx(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [evtLbIdx, openEvtId, publicEvents]);

  // Fetch public events
  useEffect(() => {
    if (!schoolId) return;
    eventsApi.getPublic(schoolId).then(d => setPublicEvents(d.events || [])).catch(() => {});
  }, [schoolId]);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!slides || slides.length <= 1) return;
    const tick = setInterval(() => setSlideIdx(i => (i + 1) % slides.length), 3600);
    return () => clearInterval(tick);
  }, [slides]);

  // scroll top on mount
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIdx === null || !slides) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setLightboxIdx(i => (i + 1) % slides.length);
      if (e.key === 'ArrowLeft')  setLightboxIdx(i => (i - 1 + slides.length) % slides.length);
      if (e.key === 'Escape')     setLightboxIdx(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIdx, slides]);

  const {
    name, address, phone, email, description,
    capacity, operatingHours, ageRange,
    facilities = [], status, locationUrl, liveStreamUrl,
  } = school;

  return (
    <div className={`sd-page${visible ? ' sd-page--in' : ''}`}>

      {/* ── Hero ─────────────────────────────────────────── */}
      <div className="sd-hero">

        {/* Slideshow */}
        {slides ? (
          <div className="sd-slideshow" aria-hidden="true">
            {slides.map((src, i) => (
              <div
                key={i}
                className={`sd-slide${i === slideIdx ? ' active' : ''}`}
                style={{ backgroundImage: `url(${src})` }}
              />
            ))}
          </div>
        ) : (
          <div className="sd-hero-fallback" aria-hidden="true">
            <img src={DEFAULT_HERO} alt="" className="sd-hero-fallback-img" />
          </div>
        )}

        {/* Gradient overlays */}
        <div className="sd-hero-gradient-top" />
        <div className="sd-hero-gradient-bot" />

        {/* Back button */}
        <button className="sd-back-btn" onClick={onBack} aria-label="Go back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span>Back</span>
        </button>

        {/* Hero text */}
        <div className="sd-hero-content">
          <span className={`sd-status-pill${status !== 'active' ? ' sd-status-pill--off' : ''}`}>
            {status === 'active' ? '🟢 Open for Enrolment' : '⚫ Currently Closed'}
          </span>
          <h1 className="sd-hero-name">{name}</h1>
          {ageRange && <p className="sd-hero-age">Ages {ageRange} · {operatingHours}</p>}
        </div>

        {/* Slide dots */}
        {slides && slides.length > 1 && (
          <div className="sd-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`sd-dot${i === slideIdx ? ' active' : ''}`}
                onClick={() => setSlideIdx(i)}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Content card ─────────────────────────────────── */}
      <div className="sd-content">

        {/* ── About ── */}
        <section className="sd-section">
          <div className="sd-section-head">
            <span className="sd-section-emoji">✨</span>
            <h2 className="sd-section-title">About</h2>
          </div>
          <p className="sd-about-text">
            {description || 'A trusted daycare and early education centre dedicated to your child\'s growth and happiness.'}
          </p>
        </section>

        {/* ── Key Details ── */}
        <section className="sd-section">
          <div className="sd-section-head">
            <span className="sd-section-emoji">📋</span>
            <h2 className="sd-section-title">Key Details</h2>
          </div>
          <div className="sd-info-grid">
            <div className="sd-info-card">
              <div className="sd-info-icon-wrap" style={{ background: '#fef3c7' }}>🕐</div>
              <div>
                <p className="sd-info-label">School Hours</p>
                <p className="sd-info-value">{operatingHours || '—'}</p>
              </div>
            </div>
            <div className="sd-info-card">
              <div className="sd-info-icon-wrap" style={{ background: '#fce7f3' }}>👶</div>
              <div>
                <p className="sd-info-label">Age Group</p>
                <p className="sd-info-value">{ageRange || '—'}</p>
              </div>
            </div>
            <div className="sd-info-card">
              <div className="sd-info-icon-wrap" style={{ background: '#dbeafe' }}>🏫</div>
              <div>
                <p className="sd-info-label">Capacity</p>
                <p className="sd-info-value">{capacity ? `${capacity} children` : '—'}</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Facilities ── */}
        {facilities.length > 0 && (
          <section className="sd-section">
            <div className="sd-section-head">
              <span className="sd-section-emoji">🏅</span>
              <h2 className="sd-section-title">Facilities</h2>
            </div>
            <div className="sd-facilities">
              {facilities.map((f, i) => {
                const c = FACILITY_COLORS[i % FACILITY_COLORS.length];
                return (
                  <span
                    key={i}
                    className="sd-facility-tag"
                    style={{ color: c, borderColor: `${c}66`, background: `${c}14` }}
                  >
                    ✅ {f}
                  </span>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Public Events ── */}
        {publicEvents.length > 0 && (
          <section className="sd-section">
            <div className="sd-section-head">
              <span className="sd-section-emoji">🎉</span>
              <h2 className="sd-section-title">Recent Events</h2>
            </div>
            <div className="sd-events-grid">
              {publicEvents.map(ev => {
                const imgs = ev.images || [];
                const previews = imgs.slice(0, 3);
                return (
                  <button
                    key={ev.id}
                    className="sd-evt-folder"
                    onClick={() => { setOpenEvtId(ev.id); setEvtLbIdx(null); }}
                    aria-label={`Open ${ev.name}`}
                  >
                    <div className="sd-evt-folder-top">
                      <div className="sd-evt-folder-tab" />
                      <div className="sd-evt-folder-body">
                        {previews.length === 0 && (
                          <span className="sd-evt-folder-empty">🖼️</span>
                        )}
                        {previews.length === 1 && (
                          <img src={previews[0].url} alt="" className="sd-evt-preview sd-evt-preview--1" loading="lazy" />
                        )}
                        {previews.length === 2 && (
                          <div className="sd-evt-preview-2up">
                            {previews.map((p, i) => <img key={i} src={p.url} alt="" className="sd-evt-preview" loading="lazy" />)}
                          </div>
                        )}
                        {previews.length >= 3 && (
                          <div className="sd-evt-preview-3up">
                            <img src={previews[0].url} alt="" className="sd-evt-preview" loading="lazy" />
                            <div className="sd-evt-preview-3up-right">
                              <img src={previews[1].url} alt="" className="sd-evt-preview" loading="lazy" />
                              <div className="sd-evt-preview-more">
                                <img src={previews[2].url} alt="" className="sd-evt-preview" loading="lazy" />
                                {imgs.length > 3 && <span className="sd-evt-preview-count">+{imgs.length - 3}</span>}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="sd-evt-folder-info">
                      <span className="sd-evt-folder-name">{ev.name}</span>
                      <span className="sd-evt-folder-meta">📅 {ev.date} &middot; {imgs.length} photo{imgs.length !== 1 ? 's' : ''}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Event Photo Collection overlay ── */}
        {openEvtId && (() => {
          const ev   = publicEvents.find(e => e.id === openEvtId);
          const imgs = ev?.images || [];
          const lbImg = evtLbIdx !== null ? imgs[evtLbIdx] : null;
          return (
            <div className="sd-evtcoll-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setEvtLbIdx(null); setOpenEvtId(null); } }}>
              <div className="sd-evtcoll-modal">
                {/* Header */}
                <div className="sd-evtcoll-header">
                  <div>
                    <div className="sd-evtcoll-title">{ev?.name}</div>
                    <div className="sd-evtcoll-subtitle">📅 {ev?.date} &middot; {imgs.length} photo{imgs.length !== 1 ? 's' : ''}</div>
                  </div>
                  <button className="sd-evtcoll-close" onClick={() => { setEvtLbIdx(null); setOpenEvtId(null); }}>✕</button>
                </div>
                {/* Photo grid */}
                <div className="sd-evtcoll-grid">
                  {imgs.map((img, i) => (
                    <div key={i} className="sd-evtcoll-item">
                      <button className="sd-evtcoll-img-btn" onClick={() => setEvtLbIdx(i)}>
                        <img src={img.url} alt={`${ev?.name} ${i + 1}`} loading="lazy" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Lightbox */}
              {lbImg && (
                <div className="sd-evtlb-wrap" onClick={(e) => { if (e.target === e.currentTarget) setEvtLbIdx(null); }}>
                  <div className="sd-evtlb-box">
                    <button className="sd-evtlb-close" onClick={() => setEvtLbIdx(null)}>✕</button>
                    {imgs.length > 1 && (
                      <button className="sd-evtlb-nav sd-evtlb-nav--prev" onClick={() => setEvtLbIdx(i => (i - 1 + imgs.length) % imgs.length)}>‹</button>
                    )}
                    <img src={lbImg.url} alt={`${ev?.name} ${evtLbIdx + 1}`} className="sd-evtlb-img" />
                    {imgs.length > 1 && (
                      <button className="sd-evtlb-nav sd-evtlb-nav--next" onClick={() => setEvtLbIdx(i => (i + 1) % imgs.length)}>›</button>
                    )}
                    <div className="sd-evtlb-counter">{evtLbIdx + 1} / {imgs.length}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Gallery ── */}
        {slides && slides.length > 0 && (
          <section className="sd-section">
            <div className="sd-section-head">
              <span className="sd-section-emoji">🖼️</span>
              <h2 className="sd-section-title">Gallery</h2>
            </div>
            <div className="sd-gallery-grid">
              {slides.map((src, i) => (
                <button
                  key={i}
                  className="sd-gallery-thumb"
                  onClick={() => setLightboxIdx(i)}
                  aria-label={`View photo ${i + 1}`}
                >
                  <img src={src} alt={`${name} photo ${i + 1}`} loading="lazy" />
                  <div className="sd-gallery-thumb-overlay">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ── Contact ── */}
        <section className="sd-section">
          <div className="sd-section-head">
            <span className="sd-section-emoji">📞</span>
            <h2 className="sd-section-title">Contact Us</h2>
          </div>
          <div className="sd-contact-list">
            {phone && (
              <div className="sd-contact-row">
                <div className="sd-contact-icon-wrap">📱</div>
                <div className="sd-contact-info">
                  <span className="sd-contact-label">Phone</span>
                  <a href={`tel:${phone}`} className="sd-contact-val">{phone}</a>
                </div>
                <a
                  href={waLink(phone, name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sd-wa-btn"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </a>
              </div>
            )}
            {email && (
              <div className="sd-contact-row">
                <div className="sd-contact-icon-wrap">✉️</div>
                <div className="sd-contact-info">
                  <span className="sd-contact-label">Email</span>
                  <a href={`mailto:${email}`} className="sd-contact-val">{email}</a>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ── Address / Location ── */}
        {(address || locationUrl) && (
          <section className="sd-section">
            <div className="sd-section-head">
              <span className="sd-section-emoji">📍</span>
              <h2 className="sd-section-title">Location</h2>
            </div>
            {address && <p className="sd-address-text">{address}</p>}
            {locationUrl && toMapEmbedUrl(locationUrl) && (
              <div className="sd-map-embed-wrap">
                <iframe
                  title="School location"
                  src={toMapEmbedUrl(locationUrl)}
                  className="sd-map-iframe"
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            )}
            {locationUrl && (
              <a
                href={locationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="sd-map-btn"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                Open in Google Maps
              </a>
            )}
          </section>
        )}

        {/* ── Live Stream ── */}
        {liveStreamUrl && (
          <section className="sd-section">
            <div className="sd-section-head">
              <span className="sd-section-emoji">🎥</span>
              <h2 className="sd-section-title">Live View</h2>
            </div>
            {hasBookingToday ? (
              <>
                <p className="sd-about-text" style={{ marginBottom: 12 }}>
                  Watch the live stream of the school premises — available to you as a booked parent.
                </p>
                <a
                  href={liveStreamUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sd-stream-btn"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
                  Watch Stream
                </a>
              </>
            ) : (
              <div className="sd-stream-locked">
                <div className="sd-stream-lock-icon">🔒</div>
                <p className="sd-stream-lock-title">Access Restricted</p>
                <p className="sd-stream-lock-sub">
                  {!activeUser
                    ? 'Sign in and book a slot for today to watch the live stream.'
                    : activeUser.userType !== 'parent'
                      ? 'Live stream is available to parents with an active booking.'
                      : 'Book a slot for today at this school to unlock the live stream.'}
                </p>
              </div>
            )}
          </section>
        )}

      </div>

      {/* ── Gallery Lightbox ── */}
      {lightboxIdx !== null && slides && (
        <div className="sd-lightbox-overlay" onClick={() => setLightboxIdx(null)}>
          <div className="sd-lightbox-box" onClick={e => e.stopPropagation()}>
            <button className="sd-lightbox-close" onClick={() => setLightboxIdx(null)} aria-label="Close">✕</button>
            <img
              src={slides[lightboxIdx]}
              alt={`${name} photo ${lightboxIdx + 1}`}
              className="sd-lightbox-img"
            />
            {slides.length > 1 && (
              <>
                <button
                  className="sd-lightbox-nav sd-lightbox-prev"
                  onClick={() => setLightboxIdx(i => (i - 1 + slides.length) % slides.length)}
                  aria-label="Previous photo"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <button
                  className="sd-lightbox-nav sd-lightbox-next"
                  onClick={() => setLightboxIdx(i => (i + 1) % slides.length)}
                  aria-label="Next photo"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
                <div className="sd-lightbox-counter">{lightboxIdx + 1} / {slides.length}</div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Sticky bottom bar ── */}      <div className="sd-sticky-bar">
        <button className="sd-sticky-back" onClick={onBack} aria-label="Go back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        {canBook && status === 'active' && (
          <button className="sd-sticky-book" onClick={() => onBook(school)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Book Slot
          </button>
        )}
      </div>
    </div>
  );
}
